/**
 * Service de stockage local pour l'application
 * Gère la persistance des données dans localStorage
 */

// Clés pour le stockage local
const STORAGE_KEYS = {
  PRODUCTS: 'stockpro_products',
  STOCK: 'stockpro_stock',
  SUPPLIERS: 'stockpro_suppliers',
  CUSTOMERS: 'stockpro_customers',
  ORDERS: 'stockpro_orders',
  SALES: 'stockpro_sales',
  PURCHASES: 'stockpro_purchases',
  WAREHOUSES: 'stockpro_warehouses',
  RETURNS: 'stockpro_returns',
  EMPLOYEES: 'stockpro_employees',
  PRODUCT_CATEGORIES: 'stockpro_product_categories',
};

/**
 * Sauvegarde des données dans le stockage local
 * @param {string} key - Clé de stockage
 * @param {any} data - Données à stocker
 */
const saveData = (key, data) => {
  try {
    const serializedData = JSON.stringify(data);
    localStorage.setItem(key, serializedData);
    return true;
  } catch (error) {
    console.error(`Erreur lors de la sauvegarde des données (${key}):`, error);
    return false;
  }
};

/**
 * Récupération des données depuis le stockage local
 * @param {string} key - Clé de stockage
 * @param {any} defaultValue - Valeur par défaut si aucune donnée n'existe
 * @returns {any} Les données récupérées ou la valeur par défaut
 */
const loadData = (key, defaultValue = []) => {
  try {
    const serializedData = localStorage.getItem(key);
    if (serializedData === null) {
      return defaultValue;
    }
    return JSON.parse(serializedData);
  } catch (error) {
    console.error(`Erreur lors de la récupération des données (${key}):`, error);
    return defaultValue;
  }
};

/**
 * Génère un ID unique pour une nouvelle entrée
 * @param {string} prefix - Préfixe pour l'ID
 * @param {Array} existingItems - Liste des éléments existants
 * @param {number} paddingSize - Nombre de chiffres pour compléter l'ID
 * @returns {string} ID généré
 */
const generateId = (prefix, existingItems, paddingSize = 3) => {
  const nextId = existingItems.length + 1;
  return `${prefix}-${String(nextId).padStart(paddingSize, '0')}`;
};

// Service pour les produits
const productService = {
  getAll: () => loadData(STORAGE_KEYS.PRODUCTS),
  
  save: (products) => saveData(STORAGE_KEYS.PRODUCTS, products),
  
  add: (product) => {
    const products = loadData(STORAGE_KEYS.PRODUCTS);
    const newProduct = { 
      ...product, 
      id: product.id || generateId('PRD', products)
    };
    products.push(newProduct);
    saveData(STORAGE_KEYS.PRODUCTS, products);
    return newProduct;
  },
  
  update: (id, updatedProduct) => {
    const products = loadData(STORAGE_KEYS.PRODUCTS);
    const index = products.findIndex(p => p.id === id);
    
    if (index !== -1) {
      products[index] = { ...products[index], ...updatedProduct };
      saveData(STORAGE_KEYS.PRODUCTS, products);
      return products[index];
    }
    return null;
  },
  
  delete: (id) => {
    const products = loadData(STORAGE_KEYS.PRODUCTS);
    const filteredProducts = products.filter(p => p.id !== id);
    saveData(STORAGE_KEYS.PRODUCTS, filteredProducts);
    return filteredProducts.length < products.length;
  }
};

// Service pour les catégories de produits
const categoryService = {
  getAll: () => loadData(STORAGE_KEYS.PRODUCT_CATEGORIES),
  
  save: (categories) => saveData(STORAGE_KEYS.PRODUCT_CATEGORIES, categories),
  
  add: (category) => {
    const categories = loadData(STORAGE_KEYS.PRODUCT_CATEGORIES);
    if (!categories.includes(category)) {
      categories.push(category);
      saveData(STORAGE_KEYS.PRODUCT_CATEGORIES, categories);
      return category;
    }
    return null;
  },
  
  update: (oldCategory, newCategory) => {
    const categories = loadData(STORAGE_KEYS.PRODUCT_CATEGORIES);
    const index = categories.indexOf(oldCategory);
    
    if (index !== -1) {
      categories[index] = newCategory;
      saveData(STORAGE_KEYS.PRODUCT_CATEGORIES, categories);
      
      // Mettre à jour la catégorie dans tous les produits
      const products = loadData(STORAGE_KEYS.PRODUCTS);
      const updatedProducts = products.map(product => 
        product.category === oldCategory ? { ...product, category: newCategory } : product
      );
      saveData(STORAGE_KEYS.PRODUCTS, updatedProducts);
      
      return newCategory;
    }
    return null;
  },
  
  delete: (category) => {
    const categories = loadData(STORAGE_KEYS.PRODUCT_CATEGORIES);
    const filteredCategories = categories.filter(c => c !== category);
    
    if (filteredCategories.length < categories.length) {
      saveData(STORAGE_KEYS.PRODUCT_CATEGORIES, filteredCategories);
      return true;
    }
    return false;
  }
};

// Service pour les fournisseurs
const supplierService = {
  getAll: () => {
    // Try to load from the preferred key first
    let suppliers = loadData(STORAGE_KEYS.SUPPLIERS);
    
    // If empty, try the legacy key
    if (!suppliers || suppliers.length === 0) {
      suppliers = loadData('suppliers', []);
      
      // If we found data in the legacy key, save it to the preferred key for future use
      if (suppliers && suppliers.length > 0) {
        saveData(STORAGE_KEYS.SUPPLIERS, suppliers);
        console.log('Migrated suppliers data from legacy key to preferred key');
      }
    }
    
    return suppliers;
  },
  
  save: (suppliers) => saveData(STORAGE_KEYS.SUPPLIERS, suppliers),
  
  add: (supplier) => {
    const suppliers = loadData(STORAGE_KEYS.SUPPLIERS);
    
    // Generate a proper ID if not provided
    let supplierId = supplier.id;
    if (!supplierId) {
      const prefix = 'SUP';
      const randomNum = Math.floor(Math.random() * 900) + 100; // Random number between 100-999
      supplierId = `${prefix}-${randomNum}`;
    }
    
    const newSupplier = { 
      ...supplier, 
      id: supplierId,
      createdAt: supplier.createdAt || new Date().toISOString()
    };
    
    suppliers.push(newSupplier);
    
    // Make sure to save to both storage keys for compatibility
    saveData(STORAGE_KEYS.SUPPLIERS, suppliers);
    saveData('suppliers', suppliers); // Legacy key
    
    console.log('Supplier added to localStorage:', newSupplier);
    return newSupplier;
  },
  
  update: (id, updatedSupplier) => {
    // Load from both possible storage keys
    let suppliers = loadData(STORAGE_KEYS.SUPPLIERS);
    
    // If empty, try the legacy key
    if (!suppliers || suppliers.length === 0) {
      suppliers = loadData('suppliers', []);
    }
    
    // Find the supplier to update
    const index = suppliers.findIndex(s => String(s.id) === String(id));
    
    if (index !== -1) {
      // Update the supplier with new data while preserving existing fields
      suppliers[index] = { 
        ...suppliers[index], 
        ...updatedSupplier,
        lastUpdated: new Date().toISOString() 
      };
      
      // Save to both storage keys for compatibility
      saveData(STORAGE_KEYS.SUPPLIERS, suppliers);
      saveData('suppliers', suppliers); // Legacy key
      
      console.log('Supplier updated in localStorage:', suppliers[index]);
      return suppliers[index];
    }
    
    console.error('Supplier not found for update:', id);
    return null;
  },
  
  delete: (id) => {
    // Load from both possible storage keys
    let suppliers = loadData(STORAGE_KEYS.SUPPLIERS);
    
    // If empty, try the legacy key
    if (!suppliers || suppliers.length === 0) {
      suppliers = loadData('suppliers', []);
    }
    
    const filteredSuppliers = suppliers.filter(s => String(s.id) !== String(id));
    
    // Save to both storage keys for compatibility
    saveData(STORAGE_KEYS.SUPPLIERS, filteredSuppliers);
    saveData('suppliers', filteredSuppliers); // Legacy key
    
    const success = filteredSuppliers.length < suppliers.length;
    
    if (success) {
      console.log('Supplier deleted from localStorage:', id);
    } else {
      console.error('Supplier not found for deletion:', id);
    }
    
    return success;
  }
};

// Service pour les clients
const customerService = {
  getAll: () => loadData(STORAGE_KEYS.CUSTOMERS),
  
  save: (customers) => saveData(STORAGE_KEYS.CUSTOMERS, customers),
  
  add: (customer) => {
    const customers = loadData(STORAGE_KEYS.CUSTOMERS);
    const newCustomer = { 
      ...customer, 
      id: customer.id || customers.length + 1
    };
    customers.push(newCustomer);
    saveData(STORAGE_KEYS.CUSTOMERS, customers);
    return newCustomer;
  },
  
  update: (id, updatedCustomer) => {
    const customers = loadData(STORAGE_KEYS.CUSTOMERS);
    const index = customers.findIndex(c => c.id === id);
    
    if (index !== -1) {
      customers[index] = { ...customers[index], ...updatedCustomer };
      saveData(STORAGE_KEYS.CUSTOMERS, customers);
      return customers[index];
    }
    return null;
  },
  
  delete: (id) => {
    const customers = loadData(STORAGE_KEYS.CUSTOMERS);
    const filteredCustomers = customers.filter(c => c.id !== id);
    saveData(STORAGE_KEYS.CUSTOMERS, filteredCustomers);
    return filteredCustomers.length < customers.length;
  }
};

// Service pour les commandes
const orderService = {
  getAll: () => loadData(STORAGE_KEYS.ORDERS),
  
  save: (orders) => saveData(STORAGE_KEYS.ORDERS, orders),
  
  add: (order) => {
    const orders = loadData(STORAGE_KEYS.ORDERS);
    const newOrder = { 
      ...order, 
      id: order.id || generateId('CMD', orders)
    };
    orders.push(newOrder);
    saveData(STORAGE_KEYS.ORDERS, orders);
    return newOrder;
  },
  
  update: (id, updatedOrder) => {
    const orders = loadData(STORAGE_KEYS.ORDERS);
    const index = orders.findIndex(o => o.id === id);
    
    if (index !== -1) {
      orders[index] = { ...orders[index], ...updatedOrder };
      saveData(STORAGE_KEYS.ORDERS, orders);
      return orders[index];
    }
    return null;
  },
  
  delete: (id) => {
    const orders = loadData(STORAGE_KEYS.ORDERS);
    const filteredOrders = orders.filter(o => o.id !== id);
    saveData(STORAGE_KEYS.ORDERS, filteredOrders);
    return filteredOrders.length < orders.length;
  }
};

// Service pour les ventes
const saleService = {
  getAll: () => loadData(STORAGE_KEYS.SALES),
  
  save: (sales) => saveData(STORAGE_KEYS.SALES, sales),
  
  add: (sale) => {
    const sales = loadData(STORAGE_KEYS.SALES);
    const newSale = { 
      ...sale, 
      id: sale.id || generateId('VNT', sales)
    };
    sales.push(newSale);
    saveData(STORAGE_KEYS.SALES, sales);
    return newSale;
  },
  
  update: (id, updatedSale) => {
    const sales = loadData(STORAGE_KEYS.SALES);
    const index = sales.findIndex(s => s.id === id);
    
    if (index !== -1) {
      sales[index] = { ...sales[index], ...updatedSale };
      saveData(STORAGE_KEYS.SALES, sales);
      return sales[index];
    }
    return null;
  },
  
  delete: (id) => {
    const sales = loadData(STORAGE_KEYS.SALES);
    const filteredSales = sales.filter(s => s.id !== id);
    saveData(STORAGE_KEYS.SALES, filteredSales);
    return filteredSales.length < sales.length;
  }
};

// Service pour les achats
const purchaseService = {
  getAll: () => loadData(STORAGE_KEYS.PURCHASES),
  
  save: (purchases) => saveData(STORAGE_KEYS.PURCHASES, purchases),
  
  add: (purchase) => {
    const purchases = loadData(STORAGE_KEYS.PURCHASES);
    const newPurchase = { 
      ...purchase, 
      id: purchase.id || generateId('ACH', purchases)
    };
    purchases.push(newPurchase);
    saveData(STORAGE_KEYS.PURCHASES, purchases);
    return newPurchase;
  },
  
  update: (id, updatedPurchase) => {
    const purchases = loadData(STORAGE_KEYS.PURCHASES);
    const index = purchases.findIndex(p => p.id === id);
    
    if (index !== -1) {
      purchases[index] = { ...purchases[index], ...updatedPurchase };
      saveData(STORAGE_KEYS.PURCHASES, purchases);
      return purchases[index];
    }
    return null;
  },
  
  delete: (id) => {
    const purchases = loadData(STORAGE_KEYS.PURCHASES);
    const filteredPurchases = purchases.filter(p => p.id !== id);
    saveData(STORAGE_KEYS.PURCHASES, filteredPurchases);
    return filteredPurchases.length < purchases.length;
  }
};

// Service pour les entrepôts
const warehouseService = {
  getAll: () => loadData(STORAGE_KEYS.WAREHOUSES),
  
  save: (warehouses) => saveData(STORAGE_KEYS.WAREHOUSES, warehouses),
  
  add: (warehouse) => {
    const warehouses = loadData(STORAGE_KEYS.WAREHOUSES);
    const newWarehouse = { 
      ...warehouse, 
      id: warehouse.id || warehouses.length + 1
    };
    warehouses.push(newWarehouse);
    saveData(STORAGE_KEYS.WAREHOUSES, warehouses);
    return newWarehouse;
  },
  
  update: (id, updatedWarehouse) => {
    const warehouses = loadData(STORAGE_KEYS.WAREHOUSES);
    const index = warehouses.findIndex(w => w.id === id);
    
    if (index !== -1) {
      warehouses[index] = { ...warehouses[index], ...updatedWarehouse };
      saveData(STORAGE_KEYS.WAREHOUSES, warehouses);
      return warehouses[index];
    }
    return null;
  },
  
  delete: (id) => {
    const warehouses = loadData(STORAGE_KEYS.WAREHOUSES);
    const filteredWarehouses = warehouses.filter(w => w.id !== id);
    saveData(STORAGE_KEYS.WAREHOUSES, filteredWarehouses);
    return filteredWarehouses.length < warehouses.length;
  }
};

// Service pour les retours
const returnService = {
  getAll: () => loadData(STORAGE_KEYS.RETURNS),
  
  save: (returns) => saveData(STORAGE_KEYS.RETURNS, returns),
  
  add: (returnItem) => {
    const returns = loadData(STORAGE_KEYS.RETURNS);
    const newReturn = { 
      ...returnItem, 
      id: returnItem.id || generateId('RET', returns)
    };
    returns.push(newReturn);
    saveData(STORAGE_KEYS.RETURNS, returns);
    return newReturn;
  },
  
  update: (id, updatedReturn) => {
    const returns = loadData(STORAGE_KEYS.RETURNS);
    const index = returns.findIndex(r => r.id === id);
    
    if (index !== -1) {
      returns[index] = { ...returns[index], ...updatedReturn };
      saveData(STORAGE_KEYS.RETURNS, returns);
      return returns[index];
    }
    return null;
  },
  
  delete: (id) => {
    const returns = loadData(STORAGE_KEYS.RETURNS);
    const filteredReturns = returns.filter(r => r.id !== id);
    saveData(STORAGE_KEYS.RETURNS, filteredReturns);
    return filteredReturns.length < returns.length;
  }
};

// Service pour les employés
const employeeService = {
  getAll: () => loadData(STORAGE_KEYS.EMPLOYEES),
  
  save: (employees) => saveData(STORAGE_KEYS.EMPLOYEES, employees),
  
  add: (employee) => {
    const employees = loadData(STORAGE_KEYS.EMPLOYEES);
    const newEmployee = { 
      ...employee, 
      id: employee.id || employees.length + 1
    };
    employees.push(newEmployee);
    saveData(STORAGE_KEYS.EMPLOYEES, employees);
    return newEmployee;
  },
  
  update: (id, updatedEmployee) => {
    const employees = loadData(STORAGE_KEYS.EMPLOYEES);
    const index = employees.findIndex(e => e.id === id);
    
    if (index !== -1) {
      employees[index] = { ...employees[index], ...updatedEmployee };
      saveData(STORAGE_KEYS.EMPLOYEES, employees);
      return employees[index];
    }
    return null;
  },
  
  delete: (id) => {
    const employees = loadData(STORAGE_KEYS.EMPLOYEES);
    const filteredEmployees = employees.filter(e => e.id !== id);
    saveData(STORAGE_KEYS.EMPLOYEES, filteredEmployees);
    return filteredEmployees.length < employees.length;
  }
};

// Service pour le stock
const stockService = {
  getAll: () => loadData(STORAGE_KEYS.STOCK),
  
  save: (stock) => saveData(STORAGE_KEYS.STOCK, stock),
  
  addMovement: (movement) => {
    console.log('Adding stock movement:', movement);
    
    // Load stock movements
    const stock = loadData('stockpro_stock');
    console.log('Current stock movements:', stock);
    
    // Create a proper movement object
    const newMovement = { 
      ...movement, 
      id: movement.id || `${movement.type.charAt(0).toUpperCase()}${movement.type.slice(1, 3)}-${new Date().getFullYear()}-${String(stock.length + 1).padStart(3, '0')}`,
      date: movement.date || new Date().toISOString().split('T')[0]
    };
    
    // Add to array
    stock.push(newMovement);
    
    // Save back to localStorage
    saveData('stockpro_stock', stock);
    console.log('Added new movement to stock:', newMovement);
    
    return newMovement;
  },
  
  deleteMovement: (id) => {
    console.log('Deleting stock movement:', id);
    
    // Load stock movements
    const stock = loadData('stockpro_stock');
    const initialLength = stock.length;
    
    // Find the movement to delete
    const movementIndex = stock.findIndex(m => m.id === id);
    
    if (movementIndex === -1) {
      console.error('Stock movement not found:', id);
      return false;
    }
    
    // Get the movement before deleting it
    const movement = stock[movementIndex];
    console.log('Found movement to delete:', movement);
    
    // Check if we need to revert stock changes
    // If the movement affected product stock, we might need to revert that change
    // This is a bit complex and would normally need business logic to determine if reversal is needed
    // For now, we'll just remove it from the list without reverting stock changes
    
    // Remove from array
    stock.splice(movementIndex, 1);
    
    // Save back to localStorage
    saveData('stockpro_stock', stock);
    console.log('Deleted movement from stock, new count:', stock.length);
    
    return stock.length < initialLength;
  },
  
  updateProductStock: (productId, movementData) => {
    const { type, quantity, warehouse, isTransfer, transferId } = movementData;
    console.log('STOCK SERVICE: Updating product stock:', productId, type, quantity, warehouse ? `in warehouse ${warehouse}` : '', isTransfer ? '(TRANSFER)' : '');
    
    try {
      // IMPORTANT: Update BOTH storage keys to ensure consistency
      // First handle 'products' key
      const products = loadData('products') || [];
      console.log('STOCK SERVICE: Current products from localStorage:', products.length);
    
    const productIndex = products.findIndex(p => p.id === productId);
      console.log('STOCK SERVICE: Found product at index:', productIndex);
    
    if (productIndex === -1) {
        console.error('STOCK SERVICE: Product not found:', productId);
      return false;
    }
    
    // Make sure we're working with numbers
    let currentStock = parseInt(products[productIndex].stock || 0);
    let changeAmount = parseInt(quantity || 0);
      console.log('STOCK SERVICE: Current stock:', currentStock, 'Change amount:', changeAmount, 'Type:', type);
    
      // Calculate new stock based on movement type
      let newStock = currentStock;
    if (type === 'entrée') {
        newStock = currentStock + changeAmount;
    } else if (type === 'sortie') {
        newStock = Math.max(0, currentStock - changeAmount);
    } else if (type === 'ajustement') {
        newStock = changeAmount;
      }
      
      console.log('STOCK SERVICE: New stock level:', newStock);
      
      // Update warehouse-specific stock if warehouse is provided
      if (warehouse) {
        // Initialize warehouseStock if it doesn't exist
        if (!products[productIndex].warehouseStock) {
          products[productIndex].warehouseStock = {};
        }
        
        // Get current warehouse stock - CRITICAL: Ensure we get the correct value
        const warehouseIdStr = String(warehouse);
        
        // SUPER DETAILED DEBUGGING - Show all warehouse stock data
        console.log(`STOCK SERVICE: DEBUG - Product ${productId} warehouse stock data:`, JSON.stringify(products[productIndex].warehouseStock));
        console.log(`STOCK SERVICE: DEBUG - Looking for warehouse ID ${warehouseIdStr} (type: ${typeof warehouseIdStr})`);
        console.log(`STOCK SERVICE: DEBUG - Warehouse keys:`, Object.keys(products[productIndex].warehouseStock || {}));
        
        // Check if the warehouse exists in warehouseStock using various type comparisons
        const warehouseKeys = Object.keys(products[productIndex].warehouseStock || {});
        for (const key of warehouseKeys) {
          console.log(`STOCK SERVICE: DEBUG - Comparing warehouse key ${key} (type: ${typeof key}) with ${warehouseIdStr} (type: ${typeof warehouseIdStr}): ${key === warehouseIdStr}`);
        }
        
        // Try both string and number lookups to diagnose the issue
        const stockAsString = products[productIndex].warehouseStock[warehouseIdStr];
        const stockAsNumber = products[productIndex].warehouseStock[parseInt(warehouseIdStr)];
        console.log(`STOCK SERVICE: DEBUG - Stock as string lookup: ${stockAsString}, Stock as number lookup: ${stockAsNumber}`);
        
        let currentWarehouseStock = parseInt(products[productIndex].warehouseStock[warehouseIdStr] || 0);
        console.log(`STOCK SERVICE: DEBUG - Current warehouse stock parsed: ${currentWarehouseStock}`);
        let newWarehouseStock = currentWarehouseStock;
        
        // Special handling for transfers
        if (isTransfer && transferId) {
          console.log(`STOCK SERVICE: TRANSFER DETECTED (${transferId}) for product ${productId} in warehouse ${warehouseIdStr}`);
          console.log(`STOCK SERVICE: Current warehouse stock: ${currentWarehouseStock}, Change amount: ${changeAmount}`);
        }
        
        // ENHANCED DEBUGGING: Log all variables before calculation
        console.log(`STOCK CALCULATION: Starting stock calculation for product ${productId}`);
        console.log(`STOCK CALCULATION: Movement type: ${type}`);
        console.log(`STOCK CALCULATION: Warehouse: ${warehouseIdStr} (type: ${typeof warehouseIdStr})`);
        console.log(`STOCK CALCULATION: Current warehouse stock: ${currentWarehouseStock} (type: ${typeof currentWarehouseStock})`);
        console.log(`STOCK CALCULATION: Change amount: ${changeAmount} (type: ${typeof changeAmount})`);
        console.log(`STOCK CALCULATION: Is transfer: ${isTransfer ? 'YES' : 'NO'}, Transfer ID: ${transferId || 'N/A'}`);
        
        // Update warehouse stock based on movement type
        if (type === 'entrée') {
          // Parse as integers to ensure proper addition
          const currentStockInt = parseInt(currentWarehouseStock) || 0;
          const changeAmountInt = parseInt(changeAmount) || 0;
          newWarehouseStock = currentStockInt + changeAmountInt;
          console.log(`STOCK CALCULATION: ENTRY - ${currentStockInt} + ${changeAmountInt} = ${newWarehouseStock}`);
        } else if (type === 'sortie') {
          // CRITICAL FIX: For transfers, make sure we properly calculate the remaining stock
          // Parse as integers to ensure proper subtraction
          const currentStockInt = parseInt(currentWarehouseStock) || 0;
          const changeAmountInt = parseInt(changeAmount) || 0;
          
          // Calculate new stock and ensure it's not negative
          const calculatedStock = currentStockInt - changeAmountInt;
          newWarehouseStock = Math.max(0, calculatedStock);
          
          console.log(`STOCK CALCULATION: EXIT - ${currentStockInt} - ${changeAmountInt} = ${calculatedStock} (limited to min 0)`); 
          console.log(`STOCK CALCULATION: Final new warehouse stock: ${newWarehouseStock}`);
          
          if (isTransfer && transferId) {
            console.log(`STOCK CALCULATION: TRANSFER EXIT DETAILS:`);
            console.log(`STOCK CALCULATION: Product ${productId} in warehouse ${warehouseIdStr}`);
            console.log(`STOCK CALCULATION: Starting stock: ${currentStockInt}`);
            console.log(`STOCK CALCULATION: Transfer amount: ${changeAmountInt}`);
            console.log(`STOCK CALCULATION: Calculated remaining: ${calculatedStock}`);
            console.log(`STOCK CALCULATION: Final stock after limits: ${newWarehouseStock}`);
            
            // CRITICAL: Alert if we're zeroing out stock when we shouldn't be
            if (newWarehouseStock === 0 && calculatedStock < 0) {
              console.warn(`STOCK CALCULATION: WARNING - Stock would be negative (${calculatedStock}) but was limited to 0`);
            }
            
            if (currentStockInt > 0 && newWarehouseStock === 0 && changeAmountInt < currentStockInt) {
              console.error(`STOCK CALCULATION: CRITICAL ERROR - Stock became zero when it should be ${currentStockInt - changeAmountInt}`);
              // Force the correct value
              newWarehouseStock = currentStockInt - changeAmountInt;
              console.log(`STOCK CALCULATION: FORCED CORRECTION - Setting stock to ${newWarehouseStock}`);
            }
          }
        } else if (type === 'ajustement') {
          newWarehouseStock = parseInt(changeAmount) || 0;
          console.log(`STOCK CALCULATION: ADJUSTMENT - Setting stock directly to ${newWarehouseStock}`);
        }
        
        // CRITICAL FIX: Ensure we're using the right key format for warehouse stock
        // First, remove any existing entries with the same warehouse ID (both string and number formats)
        // to avoid duplicate entries with different types
        if (products[productIndex].warehouseStock[parseInt(warehouseIdStr)] !== undefined) {
          console.log(`STOCK SERVICE: Found duplicate warehouse entry with number format, removing it`);
          delete products[productIndex].warehouseStock[parseInt(warehouseIdStr)];
        }
        
        // Always store with string key for consistency
        products[productIndex].warehouseStock[warehouseIdStr] = newWarehouseStock;
        console.log(`STOCK SERVICE: Updated warehouse ${warehouseIdStr} stock to ${newWarehouseStock}`);
        
        // Double-check the update was successful
        console.log(`STOCK SERVICE: Verification - Warehouse ${warehouseIdStr} stock is now:`, 
          products[productIndex].warehouseStock[warehouseIdStr]);
        
        // Update warehouseIds array if needed
        if (!products[productIndex].warehouseIds) {
          products[productIndex].warehouseIds = [];
        }
        
        // IMPORTANT: Always keep the warehouse in warehouseIds, even if stock is zero
        // This ensures the product remains associated with the warehouse
        
        // First, ensure all warehouseIds are strings for consistency
        if (products[productIndex].warehouseIds && Array.isArray(products[productIndex].warehouseIds)) {
          products[productIndex].warehouseIds = products[productIndex].warehouseIds.map(id => String(id));
        }
        
        // Check if the warehouse ID exists in the array (as a string)
        const hasWarehouse = products[productIndex].warehouseIds.some(id => String(id) === warehouseIdStr);
        if (!hasWarehouse) {
          products[productIndex].warehouseIds.push(warehouseIdStr);
          console.log(`STOCK SERVICE: Added warehouse ${warehouseIdStr} to product ${productId} warehouseIds`);
        }
        
        // Log the current warehouseIds array for verification
        console.log(`STOCK SERVICE: Product ${productId} warehouseIds:`, products[productIndex].warehouseIds);
      }
      
      // Update the product
    products[productIndex] = {
      ...products[productIndex],
        stock: newStock,
      lastUpdated: new Date().toISOString().split('T')[0]
    };
    
      // Save to 'products' key
    saveData('products', products);
      console.log('STOCK SERVICE: Updated products in localStorage');
      
      // Now do the same for 'stockpro_products' to ensure consistency
      try {
        const stockproProducts = loadData('stockpro_products') || [];
        if (stockproProducts.length > 0) {
          const stockproIndex = stockproProducts.findIndex(p => p.id === productId);
          if (stockproIndex !== -1) {
            stockproProducts[stockproIndex] = {
              ...stockproProducts[stockproIndex],
              stock: newStock,
              lastUpdated: new Date().toISOString().split('T')[0],
              // Copy warehouse data
              warehouseIds: products[productIndex].warehouseIds,
              warehouseStock: products[productIndex].warehouseStock
            };
            saveData('stockpro_products', stockproProducts);
            console.log('STOCK SERVICE: Updated stockpro_products in localStorage');
          }
        }
      } catch (stockproError) {
        console.error('STOCK SERVICE: Error updating stockpro_products:', stockproError);
      }
      
      // Create a stock movement record if it doesn't exist yet
      try {
        if (!movementData.skipMovementRecord) {
          const movementId = `MOV-${new Date().getTime()}`;
          const stockMovement = {
            id: movementId,
            date: new Date().toISOString().split('T')[0],
            type: type,
            productId: productId,
            warehouse: warehouse,
            location: warehouse,
            quantity: changeAmount,
            reference: movementData.reference || `Stock ${type}`,
            notes: movementData.notes || `Mouvement de stock ${type}`
          };
          
          const stockItems = loadData('stockpro_stock') || [];
          stockItems.push(stockMovement);
          saveData('stockpro_stock', stockItems);
          console.log('STOCK SERVICE: Created stock movement record');
        }
      } catch (movementError) {
        console.error('STOCK SERVICE: Error creating stock movement record:', movementError);
      }
      
    return true;
    } catch (error) {
      console.error('STOCK SERVICE: Critical error in updateProductStock:', error);
      return false;
    }
  }
};

// Fonction d'initialisation des données par défaut si localStorage est vide
const initializeDefaultData = () => {
  // Données par défaut pour les produits si aucune donnée n'existe
  if (!localStorage.getItem(STORAGE_KEYS.PRODUCTS)) {
    const defaultProducts = [
      {
        id: 'PROT-WHY-001',
        name: 'Protéine Whey 1kg',
        image: null,
        category: 'supplements',
        price: 3500,
        costPrice: 2500,
        taxRate: '19',
        stock: 25,
        minStockLevel: 5,
        sku: 'PROT-WHY-001',
        barcode: '123456789012',
        description: 'Protéine de whey de haute qualité, 25g de protéine par portion',
        isActive: true
      },
      {
        id: 'TSH-SPT-M',
        name: 'T-shirt Sport Homme M',
        image: null,
        category: 'clothing',
        price: 1200,
        costPrice: 800,
        taxRate: '19',
        stock: 8,
        minStockLevel: 5,
        sku: 'TSH-SPT-M',
        barcode: '123456789013',
        description: 'T-shirt respirant pour homme taille M',
        isActive: true
      },
      {
        id: 'CRT-MNH-500',
        name: 'Créatine Monohydrate 500g',
        image: null,
        category: 'supplements',
        price: 2200,
        costPrice: 1500,
        taxRate: '19',
        stock: 3,
        minStockLevel: 5,
        sku: 'CRT-MNH-500',
        barcode: '123456789014',
        description: 'Créatine monohydrate pure à 100%, 5g par portion',
        isActive: true
      },
      {
        id: 'SRV-MCF-001',
        name: 'Serviette Microfibre',
        image: null,
        category: 'accessories',
        price: 800,
        costPrice: 500,
        taxRate: '19',
        stock: 15,
        minStockLevel: 5,
        sku: 'SRV-MCF-001',
        barcode: '123456789015',
        description: 'Serviette microfibre absorbante, séchage rapide',
        isActive: true
      }
    ];
    saveData(STORAGE_KEYS.PRODUCTS, defaultProducts);
  }

  // Données par défaut pour les catégories de produits
  if (!localStorage.getItem(STORAGE_KEYS.PRODUCT_CATEGORIES)) {
    const defaultCategories = ['supplements', 'clothing', 'accessories'];
    saveData(STORAGE_KEYS.PRODUCT_CATEGORIES, defaultCategories);
  }

  // Données par défaut pour les fournisseurs
  if (!localStorage.getItem(STORAGE_KEYS.SUPPLIERS)) {
    const defaultSuppliers = [
      {
        id: 'SUP-001',
        name: 'Algérie Papier',
        contact: 'Mohamed Belkacem',
        email: 'contact@algeriepapier.dz',
        phone: '+213 561 234 567',
        address: '12 Rue des Frères, Alger',
        category: 'Papeterie',
        status: 'Active',
        orders: 23,
        lastOrder: '2023-07-15',
        rating: 4.5,
        onTimeDeliveryRate: 92,
        avgDeliveryTime: 3
      },
      {
        id: 'SUP-002',
        name: 'Tech Informatique',
        contact: 'Samira Hadj',
        email: 'info@techinformatique.dz',
        phone: '+213 557 890 123',
        address: '45 Boulevard des Martyrs, Oran',
        category: 'Informatique',
        status: 'Active',
        orders: 15,
        lastOrder: '2023-07-10',
        rating: 3.8,
        onTimeDeliveryRate: 85,
        avgDeliveryTime: 5
      },
      {
        id: 'SUP-003',
        name: 'Office Plus',
        contact: 'Karim Amir',
        email: 'commercial@officeplus.dz',
        phone: '+213 559 345 678',
        address: '22 Avenue Pasteur, Constantine',
        category: 'Fournitures',
        status: 'Active',
        orders: 18,
        lastOrder: '2023-07-12',
        rating: 4.2,
        onTimeDeliveryRate: 90,
        avgDeliveryTime: 4
      },
      {
        id: 'SUP-004',
        name: 'MeublePro',
        contact: 'Fatima Zohra',
        email: 'contact@meublepro.dz',
        phone: '+213 550 789 012',
        address: '8 Rue Ali Haddad, Sétif',
        category: 'Mobilier',
        status: 'Inactive',
        orders: 7,
        lastOrder: '2023-06-28',
        rating: 3.0,
        onTimeDeliveryRate: 75,
        avgDeliveryTime: 7
      },
      {
        id: 'SUP-005',
        name: 'Imprimerie Moderne',
        contact: 'Ahmed Benali',
        email: 'service@imprimeriemoderne.dz',
        phone: '+213 560 234 567',
        address: '36 Rue Didouche Mourad, Alger',
        category: 'Imprimerie',
        status: 'Active',
        orders: 12,
        lastOrder: '2023-07-18',
        rating: 4.7,
        onTimeDeliveryRate: 95,
        avgDeliveryTime: 2
      },
      {
        id: 'SUP-006',
        name: 'Électro Algérie',
        contact: 'Nadia Boudjellal',
        email: 'contact@electroalgerie.dz',
        phone: '+213 555 678 901',
        address: '17 Rue Larbi Ben M\'Hidi, Annaba',
        category: 'Électronique',
        status: 'Active',
        orders: 9,
        lastOrder: '2023-07-05',
        rating: 4.0,
        onTimeDeliveryRate: 88,
        avgDeliveryTime: 4
      },
      {
        id: 'SUP-007',
        name: 'Pro Nettoyage',
        contact: 'Rachid Mansouri',
        email: 'info@pronettoyage.dz',
        phone: '+213 558 123 456',
        address: '3 Boulevard Zighout Youcef, Alger',
        category: 'Entretien',
        status: 'Inactive',
        orders: 5,
        lastOrder: '2023-06-15',
        rating: 3.5,
        onTimeDeliveryRate: 80,
        avgDeliveryTime: 5
      }
    ];
    saveData(STORAGE_KEYS.SUPPLIERS, defaultSuppliers);
  }

  // Données par défaut pour les clients
  if (!localStorage.getItem(STORAGE_KEYS.CUSTOMERS)) {
    const defaultCustomers = [
      {id: 1, firstName: 'Mohamed', lastName: 'Hadj', company: 'Société Nationale', phone: '0555567890', email: 'contact@socnat.dz', address: 'Alger', status: 'actif'},
      {id: 2, firstName: 'Leila', lastName: 'Bentoumi', company: 'Clinique El Hayat', phone: '0555678901', email: 'admin@elhayat.dz', address: 'Blida', status: 'actif'},
      {id: 3, firstName: 'Ahmed', lastName: 'Kader', company: 'École Supérieure', phone: '0555789012', email: 'direction@ecolesup.dz', address: 'Alger', status: 'actif'}
    ];
    saveData(STORAGE_KEYS.CUSTOMERS, defaultCustomers);
  }

  // Initialiser les autres structures de données vides si nécessaire
  if (!localStorage.getItem(STORAGE_KEYS.ORDERS)) {
    saveData(STORAGE_KEYS.ORDERS, []);
  }
  
  if (!localStorage.getItem(STORAGE_KEYS.SALES)) {
    saveData(STORAGE_KEYS.SALES, []);
  }
  
  if (!localStorage.getItem(STORAGE_KEYS.PURCHASES)) {
    saveData(STORAGE_KEYS.PURCHASES, []);
  }
  
  // Données par défaut pour les entrepôts
  // Always ensure we have at least these default warehouses
  let hasWarehousesData = false;
  let existingWarehouses = [];
  
  try {
    const warehousesData = localStorage.getItem(STORAGE_KEYS.WAREHOUSES);
    if (warehousesData) {
      existingWarehouses = JSON.parse(warehousesData);
      hasWarehousesData = Array.isArray(existingWarehouses) && existingWarehouses.length > 0;
    }
  } catch (error) {
    console.error("Error parsing warehouses data:", error);
    hasWarehousesData = false;
  }
  
  if (!hasWarehousesData) {
    console.log("Setting default warehouses data");
    const defaultWarehouses = [
      {
        id: 1,
        name: 'Entrepôt Principal',
        location: 'Alger Centre',
        capacity: '1500 m²',
        occupancyRate: 87,
        itemCount: 2345,
        lastUpdated: '2023-06-15',
        manager: 'Ahmed Benali',
        phone: '+213 555 123 456',
        email: 'ahmed.b@entrepots.dz',
        address: '15 Rue Didouche Mourad, Alger',
        status: 'active',
        alerts: 2,
        movements: [
          { date: '2023-06-15', type: 'entrée', quantity: 120, product: 'Cartons 30x20' },
          { date: '2023-06-13', type: 'sortie', quantity: 45, product: 'Fournitures bureau' },
          { date: '2023-06-10', type: 'entrée', quantity: 200, product: 'Papier A4' },
        ]
      },
      {
        id: 2,
        name: 'Dépôt Est',
        location: 'Constantine',
        capacity: '800 m²',
        occupancyRate: 62,
        itemCount: 1280,
        lastUpdated: '2023-06-12',
        manager: 'Souad Malek',
        phone: '+213 550 987 123',
        email: 'souad.m@entrepots.dz',
        address: '22 Boulevard de l\'Est, Constantine',
        status: 'active',
        alerts: 0,
        movements: [
          { date: '2023-06-12', type: 'entrée', quantity: 85, product: 'Matériel informatique' },
          { date: '2023-06-08', type: 'sortie', quantity: 30, product: 'Écrans PC' }
        ]
      }
    ];
    saveData(STORAGE_KEYS.WAREHOUSES, defaultWarehouses);
  }
  
  if (!localStorage.getItem(STORAGE_KEYS.RETURNS)) {
    // Données par défaut pour les retours
    const defaultReturns = [
      {
        id: 'RET-F-001',
        returnNumber: 'RET-F-2023-001',
        type: 'supplier',
        entityId: 'SUP-001',
        entityName: 'Algérie Papier',
        entityEmail: 'contact@algeriepapier.dz',
        entityPhone: '+213 561 234 567',
        date: '2023-07-20',
        reason: 'defective',
        status: 'completed',
        refundMethod: 'credit',
        notes: 'Produits défectueux reçus dans la commande ACH-2023-001',
        items: [
          {
            id: 'PAP-001',
            name: 'Ramettes Papier A4',
            quantity: 2,
            price: 850,
            total: 1700
          }
        ],
        totalAmount: 1700,
        createdAt: '2023-07-20T10:15:00.000Z'
      },
      {
        id: 'RET-F-002',
        returnNumber: 'RET-F-2023-002',
        type: 'supplier',
        entityId: 'SUP-002',
        entityName: 'Tech Informatique',
        entityEmail: 'info@techinformatique.dz',
        entityPhone: '+213 557 890 123',
        date: '2023-08-05',
        reason: 'wrong_item',
        status: 'pending',
        refundMethod: 'exchange',
        notes: 'Produits reçus ne correspondent pas à la commande',
        items: [
          {
            id: 'INF-002',
            name: 'Cartouche Encre Noir',
            quantity: 3,
            price: 1200,
            total: 3600
          }
        ],
        totalAmount: 3600,
        createdAt: '2023-08-05T14:30:00.000Z'
      },
      {
        id: 'RET-C-001',
        returnNumber: 'RET-C-2023-001',
        type: 'customer',
        entityId: 'CUST-001',
        entityName: 'Ahmed Benali',
        entityEmail: 'ahmed.benali@example.com',
        entityPhone: '+213 555 123 456',
        date: '2023-08-01',
        reason: 'wrong_size',
        status: 'completed',
        refundMethod: 'cash',
        notes: 'Client insatisfait de la taille',
        items: [
          {
            id: 'TSH-SPT-M',
            name: 'T-shirt Sport Homme M',
            quantity: 1,
            price: 1200,
            total: 1200
          }
        ],
        totalAmount: 1200,
        createdAt: '2023-08-01T09:45:00.000Z'
      }
    ];
    saveData(STORAGE_KEYS.RETURNS, defaultReturns);
  }
  
  if (!localStorage.getItem(STORAGE_KEYS.EMPLOYEES)) {
    saveData(STORAGE_KEYS.EMPLOYEES, []);
  }
  
  if (!localStorage.getItem(STORAGE_KEYS.STOCK)) {
    saveData(STORAGE_KEYS.STOCK, []);
  }
};

// Exportation des services
export const LocalStorageService = {
  STORAGE_KEYS,
  initializeDefaultData,
  products: productService,
  categories: categoryService,
  suppliers: supplierService,
  customers: customerService,
  orders: orderService,
  sales: saleService,
  purchases: purchaseService,
  warehouses: warehouseService,
  returns: returnService,
  employees: employeeService,
  stock: stockService,
}; 